import { Image, Space, Tag } from "antd";
import CustomButton from "../../../components/CustomButton/Button";
import EyeIcon from "../../../assets/icons/eye-icon.svg";
import {
  formatCurrency,
  disputeAgeCalculation,
} from "../../../helpers/helpers";
import { formatPaymentDateTime } from "../helpers";
import DangerButton from "../../../components/CustomButton/DangerButton";

export const getPaymentColumns = ({
  onView,
  tabIndex,
  onApprove,
  onReject,
  onMarkDuplicate,
  canMarkDuplicate,
}) => {
  const columns = [
    {
      title: "Transaction Id",
      dataIndex:
        tabIndex === 1
          ? "appTransactionId"
          : tabIndex === 5
          ? "transactionId"
          : "txnId",
      key:
        tabIndex === 1
          ? "appTransactionId"
          : tabIndex === 5
          ? "transactionId"
          : "txnId",
      width: 200,
      fixed: "left",
    },
    {
      title: "Transaction Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      render: (text) => formatPaymentDateTime(text) || "N/A",
    },
    {
      title: "Transaction Type",
      dataIndex: tabIndex === 5 ? "transactionType" : "purpose",
      key: tabIndex === 5 ? "transactionType" : "purpose",
      width: 180,
      render: (text, record) => (
        <Space size={4} wrap>
          <span>{text || "N/A"}</span>
          {tabIndex === 1 && record?.txnChannel && <Tag>{record.txnChannel}</Tag>}
          {tabIndex === 1 && record?.isDuplicate && <Tag color="orange">Duplicate</Tag>}
        </Space>
      ),
    },
    ...(tabIndex === 1
      ? [
          {
            title: "Deal Type",
            dataIndex: "dealType",
            key: "dealType",
            width: 180,
            render: (text) => text || "-",
          },
        ]
      : []),
    {
      title: "UTR",
      dataIndex: "utr",
      key: "utr",
      width: 200,
      render: (text) => text || "N/A",
    },
    {
      title: "Beneficiary Account Number",
      dataIndex: tabIndex === 5 ? "beneficiaryAccNumber" : "beneficiaryAccNum",
      key: tabIndex === 5 ? "beneficiaryAccNumber" : "beneficiaryAccNum",
      width: 180,
      render: (text) => text || "N/A",
    },
    ...(tabIndex !== 1
      ? [
          {
            title: "Remitter Account Number",
            dataIndex: tabIndex === 5 ? "remitterAccNumber" : "remitterAccNum",
            key: tabIndex === 5 ? "remitterAccNumber" : "remitterAccNum",
            width: 180,
            render: (text) => text || "N/A",
          },
        ]
      : []),
    {
      title: "Transaction Amount",
      dataIndex: "amount",
      key: "amount",
      width: 180,
      render: (text, record) => {
        if (text == null) return "N/A";
        const isDebit = record.transactionType === "debit";
        const formattedAmount = `${isDebit ? "-" : "+"}${formatCurrency(text)}`;
        return (
          <span style={{ color: isDebit ? "#ff4d4f" : "#52c41a" }}>
            {formattedAmount}
          </span>
        );
      },
    },
    ...(tabIndex === 1 || tabIndex === 5 || tabIndex === 4
      ? [
          {
            title: "Status",
            dataIndex: "statusName",
            key: "statusName",
            width: 150,
            render: (text, record) => {
              const raw = text ?? record?.statusName ?? record?.status;
              if (tabIndex === 5 || tabIndex === 4) {
                return raw || "N/A";
              }
              if (Array.isArray(raw)) {
                return raw[0]?.statusName || "N/A";
              }
              if (raw && typeof raw === "object") {
                return raw.statusName || "N/A";
              }
              return raw || "N/A";
            },
          },
        ]
      : []),
    ...(tabIndex === 5
      ? [
          {
            title: "Remarks",
            dataIndex: "suspicionReason",
            key: "suspicionReason",
            width: 200,
            render: (text) => text || "N/A",
          },
        ]
      : []),
    ...(tabIndex !== 5
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            width: 120,
            render: (_, record) => (
              <Space size="small">
                {tabIndex === 4 ? (
                  <>
                    <CustomButton
                      color="primary"
                      variant="outlined"
                      onClick={() => onApprove?.(record)}
                    >
                      Approve
                    </CustomButton>
                    <DangerButton onClick={() => onReject?.(record)}>
                      Reject
                    </DangerButton>
                  </>
                ) : (
                  <>
                    <CustomButton
                      type="text"
                      icon={<Image src={EyeIcon} alt="View" preview={false} />}
                      onClick={() =>
                        onView?.(
                          tabIndex === 1 ? record.appTransactionId : record.txnId
                        )
                      }
                    />
                    {tabIndex === 1 && canMarkDuplicate && !record.isDuplicate && (
                      <CustomButton
                        color="primary"
                        variant="outlined"
                        onClick={() => onMarkDuplicate?.(record)}
                      >
                        Mark as Duplicate
                      </CustomButton>
                    )}
                  </>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];
  return columns;
};

export const getDisputeColumns = ({
  onEscalate,
  onResolve,
  onView,
  showEscalate,
  showResolve,
}) => {
  const columns = [
    {
      title: "Dispute ID",
      dataIndex: "disputeId",
      key: "disputeId",
      width: 200,
      fixed: "left",
    },
    {
      title: "Dispute Amount",
      dataIndex: "disputeAmount",
      key: "disputeAmount",
      width: 180,
      render: (text) => formatCurrency(text),
    },
    {
      title: "Dispute Age",
      dataIndex: "disputeDate",
      key: "disputeDate",
      width: 180,
      render: (text) => `${disputeAgeCalculation(text)} days`,
    },
    {
      title: "Dispute Reason",
      dataIndex: "disputeReasonDesc",
      key: "disputeReasonDesc",
      width: 200,
      render: (text) => text,
    },
    {
      title: "Status",
      dataIndex: "disputeStatusDesc",
      key: "disputeStatusDesc",
      width: 180,
      render: (text) => text,
    },
    {
      title: "Actions",
      key: "actions",
      width: 300,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          {showEscalate && record.disputeStatusId !== "DST1000001" && (
            <CustomButton
              type="primary"
              onClick={() => onEscalate?.(record.disputeId)}
            >
              Escalate
            </CustomButton>
          )}
          {showResolve && (
            <CustomButton
              color={
                record.disputeStatusId === "DST1000001" ? "green" : "primary"
              }
              variant="solid"
              onClick={() => {
                if (record.disputeStatusId === "DST1000001") return;
                onResolve?.(record);
              }}
            >
              {record.disputeStatusId === "DST1000001" ? "Resolved" : "Resolve"}
            </CustomButton>
          )}

          <CustomButton
            color="primary"
            variant="outlined"
            onClick={() => onView?.(record.disputeId)}
          >
            View Details
          </CustomButton>
        </Space>
      ),
    },
  ];

  return columns;
};

export const getEscalationCommentColumns = () => {
  const columns = [
    {
      title: "Comment ID",
      dataIndex: "escalationCommentId",
      key: "escalationCommentId",
      width: 200,
      fixed: "left",
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (text) => formatPaymentDateTime(text),
    },
    {
      title: "Comment Details",
      dataIndex: "escalationComment",
      key: "escalationComment",
      width: 180,
      render: (text) => text,
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      width: 200,
      render: (text) => text,
    },
  ];

  return columns;
};

export const getDisputeCommentColumns = () => {
  const columns = [
    {
      title: "Comment ID",
      dataIndex: "disputeCommentId",
      key: "disputeCommentId",
      width: 200,
      fixed: "left",
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (text) => formatPaymentDateTime(text),
    },
    {
      title: "Comment Details",
      dataIndex: "disputeComment",
      key: "disputeComment",
      width: 180,
      render: (text) => text,
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      width: 200,
      render: (text) => text,
    },
  ];

  return columns;
};

export const getDisputeRelTxnColumns = () => {
  const columns = [
    {
      title: "Transaction Date (Created Date)",
      dataIndex: "transactionDate",
      key: "transactionDate",
      width: 200,
      fixed: "left",
      render: (text) => formatPaymentDateTime(text),
    },
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
      key: "transactionId",
      width: 180,
      render: (text) => text,
    },
    {
      title: "Transaction Amount",
      dataIndex: "amount",
      key: "amount",
      width: 180,
      render: (text) => formatCurrency(text),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 200,
      render: (text) => text,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 200,
      render: (text) => text,
    },
  ];

  return columns;
};
